import React, {useState} from "react";
import type {CellObject} from "xlsx";
import {IItemModifierSale, IItemSale} from "../../models/nltx/IAnalytics";

export type SalesSortBy = 'salesSumm' | 'salesCount';
// [key the item name is looked up by, sales]
export type SalesEntry = [string, IItemSale];

// Descending by the chosen field unless `ascending`, matching the table's click-to-toggle header.
export function sortSales(map: { [key: string]: IItemSale }, sortBy: SalesSortBy, ascending: boolean): SalesEntry[] {
    const entries = Object.entries(map);
    entries.sort((a, b) => a[1][sortBy] - b[1][sortBy]);
    return ascending ? entries : entries.reverse();
}

const KIND_ORDER: Record<IItemModifierSale['kind'], number> = {variant: 0, select: 1, extra: 2};

// Sizes first, then options grouped by their group name, then extras; most sold first within each.
export function sortModifiers(mods: IItemModifierSale[]): IItemModifierSale[] {
    return [...mods].sort((a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
        || a.header.localeCompare(b.header)
        || b.salesCount - a.salesCount
        || a.label.localeCompare(b.label));
}

export function modifierText(m: IItemModifierSale): string {
    return m.kind === 'select' && m.header ? `${m.header}: ${m.label}` : m.label;
}

export type SalesSheetCell = string | number | CellObject | null;

// Sums are numbers in currency units, so the spreadsheet can add them up.
const moneyCell = (minor: number): CellObject => ({t: 'n', v: minor / 100, z: '#,##0.00'});

// The header, then one row per item followed by one row per choice with the item name repeated.
// A free choice has no sum (a null cell stays empty).
export function salesSheetRows(
    entries: SalesEntry[],
    itemName: (key: string) => string,
    header: [item: string, modifier: string, count: string, sum: string],
): SalesSheetCell[][] {
    return [
        header,
        ...entries.flatMap(([key, sale]) => [
            [itemName(key), '', sale.salesCount, moneyCell(sale.salesSumm)],
            ...sortModifiers(sale.modifiers ?? []).map(m =>
                [itemName(key), modifierText(m), m.salesCount, m.salesSumm ? moneyCell(m.salesSumm) : null]),
        ]),
    ];
}

export function SalesByItemsRows({entries, itemName, formatMoney, emptyText, expandLabel}: {
    entries: SalesEntry[],
    itemName: (key: string) => string,
    formatMoney: (minor: number) => string,
    emptyText: string,
    expandLabel: string,
}) {
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    if (entries.length === 0) {
        return <tr><td colSpan={3}>{emptyText}</td></tr>;
    }
    return (
        <>
            {entries.map(([key, sale]) => {
                const name = itemName(key);
                const hasMods = (sale.modifiers?.length ?? 0) > 0;
                const isOpen = !!expanded[key];
                return (
                    <React.Fragment key={key}>
                        <tr>
                            <td data-title="Item">
                                {hasMods && (
                                    <button
                                        type="button"
                                        aria-expanded={isOpen}
                                        aria-label={name ? `${expandLabel}: ${name}` : expandLabel}
                                        title={expandLabel}
                                        onClick={() => setExpanded({...expanded, [key]: !isOpen})}
                                        style={{border: 'none', background: 'none', padding: '0 6px 0 0', cursor: 'pointer', font: 'inherit'}}
                                    >
                                        {isOpen ? '▾' : '▸'}
                                    </button>
                                )}
                                {name}
                            </td>
                            <td data-title="Amount" className="text-right">{sale.salesCount || 0}</td>
                            <td data-title="Summ" className="text-right">{formatMoney(sale.salesSumm)}</td>
                        </tr>
                        {isOpen && sortModifiers(sale.modifiers ?? []).map(m => (
                            <tr key={`${key}|${m.kind}|${m.header}|${m.label}`} style={{color: '#6B7280'}}>
                                <td data-title="Item"><span style={{paddingLeft: '32px'}}>{modifierText(m)}</span></td>
                                <td data-title="Amount" className="text-right">{m.salesCount}</td>
                                <td data-title="Summ" className="text-right">{m.salesSumm ? formatMoney(m.salesSumm) : ''}</td>
                            </tr>
                        ))}
                    </React.Fragment>
                );
            })}
        </>
    );
}
